"use client";

import {
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  type MotionValue,
} from "framer-motion";
import { useRef, type ReactNode } from "react";

type Props = {
  children: ReactNode;
  className?: string;
  /**
   * Vertical drift (px) applied across the element's scroll range. Positive =
   * drifts down (reads as "further away"); negative = drifts up (rushes past).
   */
  distance?: number;
  /** Fade to transparent as the element scrolls out of view. */
  fade?: boolean;
  /**
   * Scroll linkage window, forwarded to framer's `useScroll`. Defaults to the
   * element travelling from entering the viewport to fully leaving the top.
   */
  offset?: NonNullable<Parameters<typeof useScroll>[0]>["offset"];
};

/**
 * Scroll-linked parallax wrapper. Maps the element's own scroll progress to a
 * spring-smoothed vertical drift (and optional fade), so layers move at
 * different rates and read with depth.
 *
 * At scroll progress 0 the transform is identity, so first paint matches the
 * static layout — no CLS, no LCP impact. Uses transform/opacity only.
 * `prefers-reduced-motion` collapses it to a plain wrapper. Client-only.
 */
export default function Parallax({
  children,
  className,
  distance = 80,
  fade = false,
  offset = ["start end", "end start"],
}: Props) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset });
  const yRaw = useTransform(scrollYProgress, [0, 1], [0, distance]);
  const y = useSpring(yRaw, { stiffness: 120, damping: 30, mass: 0.4 });
  const opacity: MotionValue<number> = useTransform(
    scrollYProgress,
    [0, 0.75],
    [1, fade ? 0 : 1],
  );

  if (reduce) return <div className={className}>{children}</div>;

  return (
    <motion.div ref={ref} className={className} style={{ y, opacity }}>
      {children}
    </motion.div>
  );
}
